import { Link, Outlet, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Phone, Mail, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Products", href: "/products" },
  { name: "Shop", href: "/shop" },
  { name: "About", href: "/about" },
  { name: "Contact", href: "/contact" },
];

export default function Layout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Top Bar */}
      <div className="bg-slate-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 flex justify-between items-center text-sm">
          <span className="hidden sm:block text-slate-400">Professional Point-of-Care Diagnostics</span>
          <div className="flex items-center gap-6">
            <a href="tel:817-508-6232" className="flex items-center gap-2 hover:text-sky-400 transition-colors" data-testid="topbar-phone">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">(817) 508-6232</span>
            </a>
            <a href="mailto:sales@clearpathrapid.com" className="flex items-center gap-2 hover:text-sky-400 transition-colors" data-testid="topbar-email">
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">sales@clearpathrapid.com</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <header className="glass-nav sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24" aria-label="Main navigation">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-10 h-10 rounded-lg bg-sky-600 flex items-center justify-center">
                <span className="text-white font-bold text-lg">CP</span>
              </div>
              <div className="hidden sm:block">
                <div className="font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>ClearPath</div>
                <div className="text-xs text-slate-500">Rapid Diagnostics</div>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? "text-sky-600"
                      : "text-slate-600 hover:text-sky-600"
                  }`}
                  data-testid={`nav-${item.name.toLowerCase()}`}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/contact" data-testid="nav-contact-sales-btn">
                <Button className="bg-sky-600 hover:bg-sky-700 text-white">
                  Contact Sales
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2 rounded-md text-slate-600 hover:bg-slate-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <>
            <div 
              className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-menu-overlay"
            />
            <div className="fixed top-0 right-0 h-full w-80 bg-white shadow-xl z-50 lg:hidden" data-testid="mobile-menu">
              <div className="p-6">
                <div className="flex justify-between items-center mb-8">
                  <span className="font-semibold text-slate-900">Menu</span>
                  <button 
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-2 rounded-md hover:bg-slate-100"
                    data-testid="mobile-menu-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <nav className="space-y-4">
                  {navigation.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center justify-between py-3 px-4 rounded-lg transition-colors ${
                        isActive(item.href)
                          ? "bg-sky-50 text-sky-600"
                          : "text-slate-600 hover:bg-slate-50"
                      }`}
                      onClick={() => setMobileMenuOpen(false)}
                      data-testid={`mobile-nav-${item.name.toLowerCase()}`}
                    >
                      {item.name}
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  ))}
                </nav>
                <div className="mt-8 pt-8 border-t border-slate-200">
                  <Link to="/contact" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-sky-600 hover:bg-sky-700 text-white" data-testid="mobile-contact-sales-btn">
                      Contact Sales
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-sky-600 flex items-center justify-center">
                  <span className="text-white font-bold text-lg">CP</span>
                </div>
                <div>
                  <div className="font-semibold">ClearPath</div>
                  <div className="text-xs text-slate-400">Rapid Diagnostics</div>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Professional distributor of point-of-care diagnostic tests for healthcare providers.
              </p>
            </div>

            {/* Products */}
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Products</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/products/strep-a" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-strep-a">
                    Strep A Test
                  </Link>
                </li>
                <li>
                  <Link to="/products/covid-flu" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-covid-flu">
                    COVID-19/Flu Combo
                  </Link>
                </li>
                <li>
                  <Link to="/products/rsv" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-rsv">
                    RSV Test
                  </Link>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Company</h3>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-about">
                    About Us
                  </Link>
                </li>
                <li>
                  <Link to="/contact" className="text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-contact">
                    Contact
                  </Link>
                </li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="font-semibold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>Contact Us</h3>
              <div className="space-y-3">
                <a href="tel:817-508-6232" className="flex items-center gap-3 text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-phone">
                  <Phone className="w-4 h-4" />
                  (817) 508-6232
                </a>
                <a href="mailto:sales@clearpathrapid.com" className="flex items-center gap-3 text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-email">
                  <Mail className="w-4 h-4" />
                  sales@clearpathrapid.com
                </a>
                <div className="pt-2 border-t border-slate-700 mt-3">
                  <p className="text-xs text-slate-500 mb-1">General Inquiries</p>
                  <a href="mailto:info@clearpathrapid.com" className="flex items-center gap-3 text-slate-400 hover:text-white text-sm transition-colors" data-testid="footer-info-email">
                    <Mail className="w-4 h-4" />
                    info@clearpathrapid.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="mt-12 pt-8 border-t border-slate-800">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm">
                © {new Date().getFullYear()} ClearPath Rapid Diagnostics. All rights reserved.
              </p>
              <p className="text-slate-500 text-xs text-center md:text-right">
                For professional in vitro diagnostic use only. Not for consumer sale.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
